import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DEFAULT_BOTTOM_TAB_COUNT,
  getAvailableBottomTabDefinitions,
  getDefaultBottomTabOrder,
  loadMobileBottomTabsConfig,
  saveMobileBottomTabsConfig,
  sanitizeBottomTabOrder,
} from '../../../utils/mobileBottomTabs';

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.4' } },
  }),
};

function TabRowContent({
  title,
  index,
  isActiveSlot,
  isDarkMode,
  isOverlay = false,
  dragHandleProps,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isActiveSlot
          ? (isDarkMode ? '#3b82f6' : '#000')
          : (isDarkMode ? '#334155' : '#e0e0e0'),
        backgroundColor: isActiveSlot
          ? (isDarkMode ? 'rgba(59, 130, 246, 0.12)' : 'rgba(0, 0, 0, 0.04)')
          : 'background.paper',
        boxShadow: isOverlay
          ? (isDarkMode
            ? '0 12px 28px rgba(0,0,0,0.45), 0 0 0 1px rgba(59,130,246,0.35)'
            : '0 12px 28px rgba(15,23,42,0.18), 0 0 0 1px rgba(0,0,0,0.08)')
          : 'none',
        transform: isOverlay ? 'scale(1.03)' : 'none',
        userSelect: 'none',
      }}
    >
      <Box
        {...(dragHandleProps || {})}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: 1.25,
          color: 'text.secondary',
          cursor: 'grab',
          touchAction: 'none',
          WebkitTapHighlightColor: 'transparent',
          flexShrink: 0,
          transition: 'background-color 0.15s ease, color 0.15s ease, transform 0.15s ease',
          '&:active': {
            cursor: 'grabbing',
            color: 'primary.main',
            backgroundColor: isDarkMode ? 'rgba(59, 130, 246, 0.18)' : 'rgba(0, 0, 0, 0.06)',
            transform: 'scale(0.96)',
          },
          '@media (hover: hover)': {
            '&:hover': {
              color: 'text.primary',
              backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            },
          },
        }}
        aria-label={`Reorder ${title}`}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Typography sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500, color: 'text.primary' }}>
        {title}
      </Typography>
      {isActiveSlot ? (
        <Chip
          label={`Tab ${index + 1}`}
          size="small"
          color="primary"
          variant="outlined"
          sx={{ height: 22, fontSize: '0.7rem' }}
        />
      ) : (
        <Chip
          label="Hidden"
          size="small"
          sx={{ height: 22, fontSize: '0.7rem', color: 'text.secondary' }}
        />
      )}
    </Box>
  );
}

function SortableTabRow({ id, title, index, isActiveSlot, isDarkMode }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      sx={{
        opacity: isDragging ? 0.35 : 1,
        borderRadius: 1.5,
        outline: isDragging
          ? (isDarkMode ? '1.5px dashed #475569' : '1.5px dashed #cbd5e1')
          : 'none',
        outlineOffset: 0,
      }}
    >
      <TabRowContent
        title={title}
        index={index}
        isActiveSlot={isActiveSlot}
        isDarkMode={isDarkMode}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </Box>
  );
}

export default function MobileBottomTabsSection({ user, isDarkMode }) {
  const availableDefinitions = React.useMemo(() => getAvailableBottomTabDefinitions(user), [user]);
  const [visible, setVisible] = React.useState(true);
  const [tabOrder, setTabOrder] = React.useState(() => getDefaultBottomTabOrder(user));
  const [activeId, setActiveId] = React.useState(null);

  React.useEffect(() => {
    const saved = loadMobileBottomTabsConfig(user?.id);
    const order = sanitizeBottomTabOrder(saved?.tabOrder, user);
    setTabOrder(order);
    setVisible(saved?.visible !== false);
  }, [user]);

  // Mouse for desktop; Touch with press-hold so page scroll still works on phones.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 220, tolerance: 10 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const persist = React.useCallback((nextVisible, nextOrder) => {
    saveMobileBottomTabsConfig(user?.id, {
      visible: nextVisible,
      tabOrder: nextOrder,
    });
  }, [user?.id]);

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(12);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    setTabOrder((prev) => {
      const oldIndex = prev.indexOf(active.id);
      const newIndex = prev.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persist(visible, next);
      return next;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleVisibleChange = (event) => {
    const nextVisible = event.target.checked;
    setVisible(nextVisible);
    persist(nextVisible, tabOrder);
  };

  const handleReset = () => {
    const defaults = getDefaultBottomTabOrder(user);
    setTabOrder(defaults);
    setVisible(true);
    persist(true, defaults);
  };

  const activeCount = Math.min(DEFAULT_BOTTOM_TAB_COUNT, availableDefinitions.length);
  const activeIndex = activeId ? tabOrder.indexOf(activeId) : -1;
  const activeDef = activeId
    ? availableDefinitions.find((item) => item.id === activeId)
    : null;

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={visible}
            onChange={handleVisibleChange}
            size="small"
          />
        }
        label={
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
              Show bottom navigation on mobile
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
              Quick tabs appear when the menu is closed. Hidden while the sidebar is open.
            </Typography>
          </Box>
        }
        sx={{ alignItems: 'flex-start', ml: 0, mb: 2 }}
      />

      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mb: 1.5 }}>
        Press and hold the handle, then drag to reorder. The first {activeCount} screens below are
        shown as bottom tabs (matching your sidebar access).
      </Typography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext items={tabOrder} strategy={verticalListSortingStrategy}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              // Soften scroll competition while a drag is active
              touchAction: activeId ? 'none' : 'auto',
            }}
          >
            {tabOrder.map((tabId, index) => {
              const def = availableDefinitions.find((item) => item.id === tabId);
              if (!def) return null;
              return (
                <SortableTabRow
                  key={tabId}
                  id={tabId}
                  title={def.title}
                  index={index}
                  isActiveSlot={index < activeCount}
                  isDarkMode={isDarkMode}
                />
              );
            })}
          </Box>
        </SortableContext>

        <DragOverlay dropAnimation={dropAnimation} zIndex={1400}>
          {activeDef && activeIndex >= 0 ? (
            <TabRowContent
              title={activeDef.title}
              index={activeIndex}
              isActiveSlot={activeIndex < activeCount}
              isDarkMode={isDarkMode}
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderColor: isDarkMode ? '#475569' : '#e0e0e0',
            color: 'text.primary',
          }}
        >
          Reset to defaults
        </Button>
      </Box>
    </Box>
  );
}
